import { DateTime } from "luxon";
import { logger } from "@/services/logger";
import { generateUUID } from "./uuid";

/**
 * Formats an ISO date string to a human-readable format
 * @param isoString - ISO 8601 date string
 * @param format - Optional format string (defaults to localized date)
 * @returns Formatted date string
 */
export const formatDate = (isoString: string, format?: string): string => {
  try {
    const dt = DateTime.fromISO(isoString);

    if (!dt.isValid) {
      logger.warn("Invalid date string provided", {
        isoString,
        correlationId: generateUUID(),
      });
      return "Invalid date";
    }

    if (format) {
      return dt.toFormat(format);
    }

    // Default format: "Jan 15, 2024"
    return dt.toLocaleString({
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    logger.error(
      "Error formatting date",
      error instanceof Error ? error : new Error(String(error)),
      {
        isoString,
        correlationId: generateUUID(),
      },
    );
    return "Invalid date";
  }
};

/**
 * Formats an ISO date string to include time
 * @param isoString - ISO 8601 date string
 * @returns Formatted date and time string
 */
export const formatDateTime = (isoString: string): string => {
  try {
    const dt = DateTime.fromISO(isoString);

    if (!dt.isValid) {
      logger.warn("Invalid date string provided", {
        isoString,
        correlationId: generateUUID(),
      });
      return "Invalid date";
    }

    // Format: "Jan 15, 2024, 2:30 PM"
    return dt.toLocaleString({
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    logger.error(
      "Error formatting datetime",
      error instanceof Error ? error : new Error(String(error)),
      {
        isoString,
        correlationId: generateUUID(),
      },
    );
    return "Invalid date";
  }
};

/**
 * Formats an ISO date string to a relative time (e.g., "2 hours ago")
 * @param isoString - ISO 8601 date string
 * @returns Relative time string
 */
export const formatRelativeTime = (isoString: string): string => {
  try {
    const dt = DateTime.fromISO(isoString);

    if (!dt.isValid) {
      logger.warn("Invalid date string provided", {
        isoString,
        correlationId: generateUUID(),
      });
      return "Invalid date";
    }

    return dt.toRelative() || "Unknown time";
  } catch (error) {
    logger.error(
      "Error formatting relative time",
      error instanceof Error ? error : new Error(String(error)),
      {
        isoString,
        correlationId: generateUUID(),
      },
    );
    return "Invalid date";
  }
};

/**
 * Checks if a date string is valid ISO format
 * @param isoString - ISO 8601 date string
 * @returns Boolean indicating if the date is valid
 */
export const isValidISODate = (isoString: string): boolean => {
  try {
    const dt = DateTime.fromISO(isoString);
    return dt.isValid;
  } catch {
    return false;
  }
};

/**
 * Converts an ISO date string to a specific timezone
 * @param isoString - ISO 8601 date string
 * @param timezone - Target timezone (e.g., 'America/New_York')
 * @returns Formatted date string in the target timezone
 */
export const formatDateInTimezone = (
  isoString: string,
  timezone: string,
): string => {
  try {
    const dt = DateTime.fromISO(isoString).setZone(timezone);

    if (!dt.isValid) {
      logger.warn("Invalid date string provided", {
        isoString,
        timezone,
        correlationId: generateUUID(),
      });
      return "Invalid date";
    }

    return dt.toLocaleString({
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch (error) {
    logger.error(
      "Error formatting date in timezone",
      error instanceof Error ? error : new Error(String(error)),
      {
        isoString,
        timezone,
        correlationId: generateUUID(),
      },
    );
    return "Invalid date";
  }
};
